import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  InputAdornment,
  Divider,
  Collapse,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  LocalOffer as TagIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Timer as TimerIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ReactivateIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { format, formatDistanceToNow, isPast } from 'date-fns';

// Stat card component
const StatCard = ({ icon, label, value, subValue, color = 'primary' }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{ color: `${color}.main`, mb: 1 }}>{icon}</Box>
      <Typography variant="h4" fontWeight={600}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {subValue && (
        <Typography variant="caption" color="text.secondary">{subValue}</Typography>
      )}
    </CardContent>
  </Card>
);

const PromoCodes = () => {
  // Data state
  const [stats, setStats] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deactivateDialog, setDeactivateDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [redemptionsDialog, setRedemptionsDialog] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionsLoading, setRedemptionsLoading] = useState(false);
  const [editName, setEditName] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Filter state
  const [filterActive, setFilterActive] = useState('all');
  const [searchCode, setSearchCode] = useState('');

  // Create form state
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percent',
    discountValue: '',
    duration: 'once',
    durationInMonths: 1,
    maxRedemptions: '',
    expiresAt: '',
    firstTimeOnly: false,
    name: '',
    notes: '',
  });
  const [creating, setCreating] = useState(false);

  // Preset buttons
  const presets = [
    { label: 'Beta (100% × 3mo)', code: 'BETA', discountType: 'percent', discountValue: 100, duration: 'repeating', durationInMonths: 3 },
    { label: 'Friend (50% × 1mo)', code: 'FRIEND', discountType: 'percent', discountValue: 50, duration: 'once' },
    { label: 'Launch (25% forever)', code: 'LAUNCH', discountType: 'percent', discountValue: 25, duration: 'forever' },
    { label: '$5 off × 1mo', code: 'SAVE5', discountType: 'amount', discountValue: 5, duration: 'once' },
  ];

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [statsRes, codesRes] = await Promise.all([
        adminAPI.getPromoCodeStats(),
        adminAPI.listPromoCodes({ 
          active: filterActive !== 'all' ? filterActive : undefined,
          search: searchCode || undefined,
        }),
      ]);

      setStats(statsRes.data);
      setPromoCodes(codesRes.data.promoCodes);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, [filterActive, searchCode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle form input
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Apply preset
  const applyPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      code: preset.code + Math.random().toString(36).substring(2, 6).toUpperCase(),
      discountType: preset.discountType,
      discountValue: preset.discountValue,
      duration: preset.duration,
      durationInMonths: preset.durationInMonths || 1,
    }));
  };

  // Create promo code
  const handleCreate = async () => {
    try {
      setCreating(true);
      setError('');

      // Validation
      if (!formData.code) {
        setError('Promo code is required');
        return;
      }
      if (!formData.discountValue) {
        setError('Discount value is required');
        return;
      }

      const payload = {
        code: formData.code,
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        duration: formData.duration,
        durationInMonths: formData.duration === 'repeating' ? parseInt(formData.durationInMonths) : undefined,
        maxRedemptions: formData.maxRedemptions ? parseInt(formData.maxRedemptions) : undefined,
        expiresAt: formData.expiresAt || undefined,
        firstTimeOnly: formData.firstTimeOnly,
        name: formData.name || undefined,
        notes: formData.notes || undefined,
      };

      await adminAPI.createPromoCode(payload);
      
      setSuccess(`Promo code "${formData.code}" created successfully!`);
      setCreateDialogOpen(false);
      setFormData({
        code: '',
        discountType: 'percent',
        discountValue: '',
        duration: 'once',
        durationInMonths: 1,
        maxRedemptions: '',
        expiresAt: '',
        firstTimeOnly: false,
        name: '',
        notes: '',
      });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create promo code');
    } finally {
      setCreating(false);
    }
  };

  // Deactivate promo code
  const handleDeactivate = async (reason) => {
    try {
      await adminAPI.deactivatePromoCode(deactivateDialog.id, reason);
      setSuccess(`Promo code "${deactivateDialog.code}" has been deactivated`);
      setDeactivateDialog(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate promo code');
    }
  };

  // Load redemptions
  const loadRedemptions = async (promoCode) => {
    try {
      setRedemptionsDialog(promoCode);
      setRedemptionsLoading(true);
      const res = await adminAPI.getPromoCodeRedemptions(promoCode.id);
      setRedemptions(res.data.redemptions);
    } catch (err) {
      setError('Failed to load redemptions');
    } finally {
      setRedemptionsLoading(false);
    }
  };

  // Reactivate promo code
  const handleReactivate = async (promo) => {
    try {
      await adminAPI.reactivatePromoCode(promo.id);
      setSuccess(`Promo code "${promo.code}" has been reactivated`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reactivate promo code');
    }
  };

  // Open edit dialog
  const openEditDialog = (promo) => {
    setEditDialog(promo);
    setEditName(promo.metadata?.name || '');
    setEditNotes(promo.metadata?.notes || '');
  };

  // Save edit
  const handleEditSave = async () => {
    try {
      setEditSaving(true);
      await adminAPI.updatePromoCode(editDialog.id, {
        name: editName,
        notes: editNotes,
      });
      setSuccess(`Promo code "${editDialog.code}" has been updated`);
      setEditDialog(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update promo code');
    } finally {
      setEditSaving(false);
    }
  };

  // Delete promo code
  const handleDelete = async () => {
    try {
      const deletedId = deleteDialog.id;
      await adminAPI.deletePromoCode(deletedId);
      setSuccess(`Promo code "${deleteDialog.code}" has been deleted`);
      setDeleteDialog(null);
      setDeleteConfirmText('');
      setPromoCodes(prev => prev.filter(p => p.id !== deletedId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete promo code');
    }
  };

  // Copy to clipboard
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setSuccess(`Copied "${code}" to clipboard`);
  };

  // Get status chip
  const getStatusChip = (promo) => {
    if (!promo.active) {
      return <Chip label="Inactive" size="small" color="default" />;
    }
    if (promo.isExpired) {
      return <Chip label="Expired" size="small" color="error" />;
    }
    if (promo.isMaxedOut) {
      return <Chip label="Fully Used" size="small" color="warning" />;
    }
    return <Chip label="Active" size="small" color="success" />;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>Promo Codes</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Code
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <StatCard
              icon={<TagIcon fontSize="large" />}
              label="Total Codes"
              value={stats.totalCodes}
              color="primary"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              icon={<CheckIcon fontSize="large" />}
              label="Active Codes"
              value={stats.activeCodes}
              color="success"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              icon={<TrendingUpIcon fontSize="large" />}
              label="Total Redemptions"
              value={stats.totalRedemptions}
              color="info"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              icon={<PeopleIcon fontSize="large" />}
              label="One-Off Codes"
              value={stats.oneOffCodes}
              subValue="Single use codes"
              color="secondary"
            />
          </Grid>
        </Grid>
      )}

      {/* Top Codes */}
      {stats?.topCodes?.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Top Performing Codes</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {stats.topCodes.slice(0, 5).map((code, i) => (
              <Chip
                key={i}
                label={`${code.code}: ${code.redemptions} uses`}
                variant="outlined"
                color="primary"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Search by code"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && loadData()}
              placeholder="e.g., BETA50"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterActive}
                label="Status"
                onChange={(e) => setFilterActive(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Active Only</MenuItem>
                <MenuItem value="false">Inactive Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="outlined" onClick={loadData} fullWidth>
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Promo Codes Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Expires</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : promoCodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No promo codes found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                promoCodes.map((promo) => (
                  <TableRow key={promo.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={600} fontFamily="monospace">
                          {promo.code}
                        </Typography>
                        <Tooltip title="Copy code">
                          <IconButton size="small" onClick={() => copyCode(promo.code)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {promo.isOneOff && (
                          <Chip label="One-off" size="small" variant="outlined" />
                        )}
                      </Box>
                      {promo.metadata?.name && (
                        <Typography variant="caption" color="text.secondary">
                          {promo.metadata.name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography>{promo.discountDescription}</Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(promo)}</TableCell>
                    <TableCell>
                      <Typography>
                        {promo.timesRedeemed}
                        {promo.maxRedemptions && ` / ${promo.maxRedemptions}`}
                      </Typography>
                      {promo.timesRedeemed > 0 && (
                        <Button
                          size="small"
                          onClick={() => loadRedemptions(promo)}
                          sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                        >
                          View details
                        </Button>
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {promo.expiresAt ? (
                        <Box>
                          <Typography variant="body2">
                            {format(new Date(promo.expiresAt), 'MMM d, yyyy')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {isPast(new Date(promo.expiresAt))
                              ? 'Expired'
                              : formatDistanceToNow(new Date(promo.expiresAt), { addSuffix: true })}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography color="text.secondary">Never</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2">
                        {format(new Date(promo.created), 'MMM d, yyyy')}
                      </Typography>
                      {promo.metadata?.created_by && (
                        <Typography variant="caption" color="text.secondary">
                          by {promo.metadata.created_by}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditDialog(promo)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {promo.active && !promo.isExpired && !promo.isMaxedOut ? (
                          <Tooltip title="Deactivate">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => setDeactivateDialog(promo)}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : !promo.active && !promo.isExpired && !promo.isMaxedOut ? (
                          <Tooltip title="Reactivate">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReactivate(promo)}
                            >
                              <ReactivateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {promo.timesRedeemed === 0 && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialog(promo)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Promo Code</DialogTitle>
        <DialogContent>
          {/* Quick Presets */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
            Quick Presets
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {presets.map((preset, i) => (
              <Chip
                key={i}
                label={preset.label}
                onClick={() => applyPreset(preset)}
                variant="outlined"
                clickable
              />
            ))}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {/* Code */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Promo Code"
                value={formData.code}
                onChange={(e) => handleFormChange('code', e.target.value.toUpperCase().replace(/\s/g, ''))}
                placeholder="e.g., BETA50, FRIENDS2026"
                helperText="Letters, numbers, underscores, hyphens only"
                inputProps={{ style: { fontFamily: 'monospace', fontWeight: 'bold' } }}
              />
            </Grid>

            {/* Name/Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Internal Name/Description"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                placeholder="e.g., Beta testers from Product Hunt"
              />
            </Grid>

            {/* Discount Type */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Discount Type</InputLabel>
                <Select
                  value={formData.discountType}
                  label="Discount Type"
                  onChange={(e) => handleFormChange('discountType', e.target.value)}
                >
                  <MenuItem value="percent">Percentage Off</MenuItem>
                  <MenuItem value="amount">Fixed Amount Off</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Discount Value */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={formData.discountType === 'percent' ? 'Percent Off' : 'Amount Off'}
                type="number"
                value={formData.discountValue}
                onChange={(e) => handleFormChange('discountValue', e.target.value)}
                InputProps={{
                  startAdornment: formData.discountType === 'amount' 
                    ? <InputAdornment position="start">$</InputAdornment>
                    : null,
                  endAdornment: formData.discountType === 'percent'
                    ? <InputAdornment position="end">%</InputAdornment>
                    : null,
                }}
                inputProps={{ 
                  min: formData.discountType === 'percent' ? 1 : 0.5, 
                  max: formData.discountType === 'percent' ? 100 : undefined 
                }}
              />
            </Grid>

            {/* Duration */}
            <Grid item xs={formData.duration === 'repeating' ? 6 : 12}>
              <FormControl fullWidth>
                <InputLabel>Duration</InputLabel>
                <Select
                  value={formData.duration}
                  label="Duration"
                  onChange={(e) => handleFormChange('duration', e.target.value)}
                >
                  <MenuItem value="once">Once (first month only)</MenuItem>
                  <MenuItem value="repeating">Multiple Months</MenuItem>
                  <MenuItem value="forever">Forever</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Duration in Months */}
            {formData.duration === 'repeating' && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Number of Months"
                  type="number"
                  value={formData.durationInMonths}
                  onChange={(e) => handleFormChange('durationInMonths', e.target.value)}
                  inputProps={{ min: 1, max: 36 }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Limits (Optional)
              </Typography>
            </Grid>

            {/* Max Redemptions */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Max Redemptions"
                type="number"
                value={formData.maxRedemptions}
                onChange={(e) => handleFormChange('maxRedemptions', e.target.value)}
                placeholder="Unlimited"
                helperText="Leave empty for unlimited, 1 for one-off"
                inputProps={{ min: 1 }}
              />
            </Grid>

            {/* Expires At */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Expires On"
                type="date"
                value={formData.expiresAt}
                onChange={(e) => handleFormChange('expiresAt', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty for no expiration"
              />
            </Grid>

            {/* First Time Only */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.firstTimeOnly}
                    onChange={(e) => handleFormChange('firstTimeOnly', e.target.checked)}
                  />
                }
                label="First-time customers only"
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Internal Notes"
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                multiline
                rows={2}
                placeholder="Any additional notes about this code..."
              />
            </Grid>
          </Grid>

          {/* Preview */}
          {formData.code && formData.discountValue && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>Preview</Typography>
              <Typography>
                <strong>{formData.code}</strong>: {' '}
                {formData.discountType === 'percent' 
                  ? `${formData.discountValue}% off` 
                  : `$${formData.discountValue} off`}
                {formData.duration === 'once' && ' (first month)'}
                {formData.duration === 'repeating' && ` for ${formData.durationInMonths} month${formData.durationInMonths > 1 ? 's' : ''}`}
                {formData.duration === 'forever' && ' (forever)'}
              </Typography>
              {formData.maxRedemptions && (
                <Typography variant="body2" color="text.secondary">
                  Limited to {formData.maxRedemptions} use{formData.maxRedemptions > 1 ? 's' : ''}
                </Typography>
              )}
              {formData.expiresAt && (
                <Typography variant="body2" color="text.secondary">
                  Expires: {format(new Date(formData.expiresAt), 'MMMM d, yyyy')}
                </Typography>
              )}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreate}
            disabled={creating || !formData.code || !formData.discountValue}
          >
            {creating ? <CircularProgress size={20} /> : 'Create Code'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Dialog */}
      <Dialog open={!!deactivateDialog} onClose={() => setDeactivateDialog(null)}>
        <DialogTitle>Deactivate Promo Code</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to deactivate <strong>{deactivateDialog?.code}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This code has been used {deactivateDialog?.timesRedeemed || 0} time(s).
            You can reactivate it later if needed.
          </Typography>
          <TextField
            fullWidth
            label="Reason (optional)"
            id="deactivate-reason"
            sx={{ mt: 2 }}
            placeholder="e.g., Campaign ended, Replacing with new code..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialog(null)}>Cancel</Button>
          <Button 
            color="error" 
            variant="contained"
            onClick={() => {
              const reason = document.getElementById('deactivate-reason')?.value || '';
              handleDeactivate(reason);
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Promo Code: {editDialog?.code}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            You can update the name and notes. Discount type, amount, and duration cannot be changed after creation.
          </Alert>
          <TextField
            fullWidth
            label="Internal Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="e.g., Beta testers from Product Hunt"
          />
          <TextField
            fullWidth
            label="Internal Notes"
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Any additional notes..."
          />
          {editDialog && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>Code Details (read-only)</Typography>
              <Typography variant="body2">Discount: {editDialog.discountDescription}</Typography>
              <Typography variant="body2">Duration: {editDialog.coupon?.duration}{editDialog.coupon?.durationInMonths ? ` (${editDialog.coupon.durationInMonths} months)` : ''}</Typography>
              <Typography variant="body2">Usage: {editDialog.timesRedeemed}{editDialog.maxRedemptions ? ` / ${editDialog.maxRedemptions}` : ' (unlimited)'}</Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editSaving}
          >
            {editSaving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => { setDeleteDialog(null); setDeleteConfirmText(''); }}>
        <DialogTitle>Delete Promo Code</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            This will permanently delete the promo code and its underlying Stripe coupon. This action cannot be undone.
          </Alert>
          <Typography gutterBottom>
            To confirm, type <strong>{deleteDialog?.code}</strong> below:
          </Typography>
          <TextField
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={deleteDialog?.code}
            sx={{ mt: 1 }}
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialog(null); setDeleteConfirmText(''); }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteConfirmText !== deleteDialog?.code}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redemptions Dialog */}
      <Dialog 
        open={!!redemptionsDialog} 
        onClose={() => setRedemptionsDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Redemptions for {redemptionsDialog?.code}
        </DialogTitle>
        <DialogContent>
          {redemptionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : redemptions.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No redemption details available
            </Typography>
          ) : (
            <List>
              {redemptions.map((r, i) => (
                <ListItem key={i} divider>
                  <ListItemText
                    primary={r.customerEmail || r.customerName || 'Unknown customer'}
                    secondary={`Redeemed ${format(new Date(r.redeemedAt), 'MMM d, yyyy')} • Status: ${r.status}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedemptionsDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromoCodes;